// isLoudDevRuntime.js — local copy of the pure, zero-dependency helper from
// `smbls/packages/utils/env.js` (added in 62baffcc7, first published as
// `@symbo.ls/utils@3.14.691`).
//
// Why a local copy instead of bumping `@symbo.ls/utils`: the reported
// `SyntaxError: Export named 'isLoudDevRuntime' not found` was real (the
// floor at bun.lock time, 3.14.663, predates the symbol), but raising the
// floor to pull it in caused second-order breakage — see the revert commit
// "Revert \"fix(deps): declare the @symbo.ls/utils floor where
// isLoudDevRuntime is used\"" for the measured before/after test tallies.
// This function has zero dependencies (only reads `process.env` /
// `location.hostname`), so vendoring it locally closes the SyntaxError
// without touching the dependency graph at all. Once the lock's
// `@symbo.ls/utils` floor naturally advances past 3.14.691, this file and
// its three call sites can be repointed back to the package export.
//
// FRAMEWORK-6: shared gate for dev-only loud-failure diagnostics.
//
// Why a hybrid gate instead of `process.env.NODE_ENV !== 'production'` alone:
// the dist build (`esbuild --minify`, default platform=browser) auto-defines
// `process.env.NODE_ENV` to `"production"` and dead-code-eliminates any
// branch gated on it. Consumers resolve `dist/esm`, not `src`, so a
// NODE_ENV-only gate can never fire in a real dev browser session.
//
// Why not hostname alone: node/test consumers (vitest, bun test, SSR) resolve
// source, where NODE_ENV is live and `location` may not exist.
//
// So:
//  - build-time leg — `process.env.NODE_ENV` — live for source/test
//    consumers; folds to `false` at dist build time.
//  - runtime leg — `location.hostname` is `localhost`, `127.0.0.1`, or any
//    `*.localhost` — survives the dist build, so dev servers
//    (`workspace.localhost:1355` etc.) still get loud failures.
//
// A visitor on a production domain (`my.symbols.app`, `*.symbo.ls`, custom
// domains) matches neither leg and never sees console noise from this.
//
// @param {object} [overrides] test-only injection: `{ env, hostname }`
// @returns {boolean}
export const isLoudDevRuntime = (overrides) => {
  const env =
    overrides && 'env' in overrides
      ? overrides.env
      : typeof process !== 'undefined' && process.env
        ? process.env.NODE_ENV
        : undefined
  if (env && env !== 'production') return true

  const hostname =
    overrides && 'hostname' in overrides
      ? overrides.hostname
      : typeof location !== 'undefined'
        ? location.hostname
        : undefined
  if (typeof hostname === 'string' && hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')
  }
  return false
}
